import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal, { ModalNavBar } from '@node-space/storybook-components/dist/Modal'
import {
  ActionList,
  Avatar,
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  Icon,
  Text,
} from '@node-space/ui-components'
import { numberWithCommas } from '@node-space/utils'
import { useMerchantsQuery } from 'hooks/queries/useMerchantsQuery'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { MerchantType } from 'types/types'
import { MerchantDetailsSummary } from './MerchantDetailsSummary'
import { MerchantEdit } from './MerchantEdit'

export const MerchantTable = () => {
  const { t } = useTranslation()

  const { data: { merchantList = [] } = {} } = useMerchantsQuery()

  const permissions = useMappedRoles()?.manageMerchants
  const canEdit = permissions?.canEdit

  const [isModalEditOpen, setModalEditOpen] = useState(false)
  const [isModalDetailsOpen, setModalDetailsOpen] = useState(false)
  const [selectedDetailsMerchant, setSelectedDetailMerchant] = useState<MerchantType>(null)
  const [selectedEditMerchant, setSelectedEditMerchant] = useState<MerchantType>(null)

  const handleOpenEditModal = (merchant: MerchantType) => {
    setSelectedEditMerchant(merchant)
    setModalEditOpen(true)
  }

  const handleCloseEditModal = () => {
    setSelectedEditMerchant(null)
    setModalEditOpen(false)
  }

  const handleOpenDetailsModal = (merchant: MerchantType) => {
    setSelectedDetailMerchant(merchant)
    setModalDetailsOpen(true)
  }

  const handleRowClick = (merchant: MerchantType) => {
    setSelectedDetailMerchant(merchant)
    setModalDetailsOpen(true)
  }

  const handleCloseDetailModal = () => {
    setModalDetailsOpen(false)
    setSelectedDetailMerchant(null)
  }

  const merchantActionList = useMemo(() => {
    if (!merchantList) return []

    return merchantList.map((merchant: MerchantType) => {
      const wallet = merchant?.wallet

      return {
        key: merchant?.id,
        id: `wallet-row-${merchant?.id}`,
        title: merchant?.displayName,
        testid: `wallet-row-${merchant?.id}`,
        BodyPre: (
          <Text variant="body-label" color="faint">
            {wallet?.description}
          </Text>
        ),
        BodyPost: (
          <Text variant="body-label" color="faint">
            {wallet?.currency?.code}
          </Text>
        ),
        BodyAvatar: <Avatar name={wallet?.currency?.code} size="tiny" />,
        onItemClick: (e: { stopPropagation: () => void }) => {
          e?.stopPropagation()
          handleRowClick(merchant)
        },
        RightPrimaryText: wallet?.balance
          ? `${numberWithCommas(wallet.balance)} ${wallet?.currency?.code}`
          : '-',
        CustomAction: (
          <DropdownMenu
            DropdownMenuItems={[
              canEdit ? (
                <>
                  <DropdownMenuItem
                    key={`${merchant?.wallet?.id}-details`}
                    LeadingIcon={<Icon name="Info" />}
                    onClick={() => handleOpenDetailsModal(merchant)}
                  >
                    {t('Details')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    key={`${merchant?.wallet?.id}-edit`}
                    LeadingIcon={<Icon name="SimplePencilCustom" size="small" />}
                    onClick={() => handleOpenEditModal(merchant)}
                  >
                    {t('Edit')}
                  </DropdownMenuItem>
                </>
              ) : (
                <DropdownMenuItem
                  key={`${merchant?.wallet?.id}-details`}
                  LeadingIcon={<Icon name="Info" size="small" />}
                  onClick={() => handleOpenDetailsModal(merchant)}
                >
                  {t('Details')}
                </DropdownMenuItem>
              ),
            ]}
          >
            <Button iconElement={<Icon name="DotsThreeVerticalCustom" />} variant="ghost" />
          </DropdownMenu>
        ),
      }
    })
  }, [merchantList])

  return (
    <Box className="overflow-y-hidden">
      <ActionList id="manage-merchants" items={merchantActionList} />

      {selectedEditMerchant && (
        <MerchantEdit
          merchant={selectedEditMerchant}
          isModalOpen={isModalEditOpen}
          handleCloseModal={handleCloseEditModal}
        />
      )}

      {selectedDetailsMerchant && (
        <Modal allowOverFlow visible={isModalDetailsOpen} closeModal={handleCloseDetailModal} testid='merchant-summary-modal'>
          <ModalNavBar
            title={selectedDetailsMerchant?.displayName}
            onClose={handleCloseDetailModal}
          />

          <MerchantDetailsSummary merchant={selectedDetailsMerchant} />
        </Modal>
      )}
    </Box>
  )
}
